import React, { useState, useMemo, useEffect } from 'react';
import Layout, { PageKey } from '../components/Layout';
import { getAdAnalysis, getWpStatus, getRealLeadsAgg } from '../api';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
  onOpenAccount: (accountId: string, accountName: string) => void;
}

interface RealAgg {
  total_meta_form_leads: number;
  total_fluentform_leads: number;
  total_real_leads: number;
  total_spend: number;
  real_cpl: number | null;
  partners: Record<string, { meta_form_leads: number; fluentform_leads: number; total_leads: number; spend: number; real_cpl: number | null }>;
}

interface AnalysisAd {
  ad_id: string;
  ad_name: string;
  account_id: string;
  partner: string;
  campaign_name: string;
  status: string;
  leads: number;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  cost_per_lead: number | null;
  runtime_days: number;
  thumbnail_url: string | null;
}

interface PartnerAgg {
  account_id: string;
  partner: string;
  leads: number;
  spend: number;
  impressions: number;
  clicks: number;
  adCount: number;
  activeCount: number;
  ctr: number;
  cpl: number | null;
  bestAd: AnalysisAd | null;
}

type RangeKey = 'maximum' | 'last_60d' | '2026' | '2025';
type PartnerSort = 'leads' | 'cpl';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'maximum', label: 'Gesamte Laufzeit' },
  { key: 'last_60d', label: 'Letzte 60 Tage' },
  { key: '2026', label: '2026' },
  { key: '2025', label: '2025' },
];

// Cache für echte Lead-Daten pro Suche (Pattern|Zeitraum) – überlebt Navigation
const aggCache = new Map<string, RealAgg>();

// ── Format-Helfer (de-DE) ───────────────────────────────────────────────────────
const nf = (n: number) => Math.round(n || 0).toLocaleString('de-DE');
const eur = (n: number | null | undefined) =>
  (n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
const pct = (n: number | null | undefined) => (n == null ? '–' : n.toFixed(2).replace('.', ',') + ' %');

export default function Analyse({ user, onLogout, onNavigate, onOpenAccount }: Props) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [range, setRange] = useState<RangeKey>('maximum');
  const [onlyActive, setOnlyActive] = useState(false);
  const [ads, setAds] = useState<AnalysisAd[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [partnerSort, setPartnerSort] = useState<PartnerSort>('leads');
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [showAllWinners, setShowAllWinners] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [wpStatus, setWpStatus] = useState<Record<string, { has_config: boolean; wp_status: string; wp_status_msg?: string; wp_url?: string }>>({});
  const [realAgg, setRealAgg] = useState<RealAgg | null>(null);
  const [realLoading, setRealLoading] = useState(false);

  useEffect(() => { getWpStatus().then(r => setWpStatus(r.data || {})).catch(() => {}); }, []);
  const wpState = (id: string): 'connected' | 'error' | 'none' => {
    const e = wpStatus[id];
    if (!e || !e.has_config) return 'none';
    return e.wp_status === 'connected' ? 'connected' : 'error';
  };

  const runSearch = async (q: string, r: RangeKey) => {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    setError('');
    setSubmitted(term);
    setSelectedPartner(null);
    setExcluded([]);
    setShowAllWinners(false);
    setShowTable(false);
    // Gecachte echte Leads sofort zeigen (sonst Fallback = bisherige Meta-Zahlen),
    // dann im Hintergrund aktualisieren.
    const cacheKey = `${term}|${r}`;
    setRealAgg(aggCache.get(cacheKey) || null);
    try {
      const res = await getAdAnalysis(term, r);
      const resultAds: AnalysisAd[] = res.data.ads || [];
      setAds(resultAds);
      const accIds = Array.from(new Set(resultAds.map(a => a.account_id)));
      if (accIds.length) {
        setRealLoading(true);
        getRealLeadsAgg(accIds, term, r)
          .then(rr => { aggCache.set(cacheKey, rr.data); setRealAgg(rr.data); })
          .catch(() => {})
          .finally(() => setRealLoading(false));
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Analyse fehlgeschlagen');
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = () => runSearch(query, range);
  const changeRange = (r: RangeKey) => { setRange(r); if (submitted) runSearch(submitted, r); };

  // ── Aggregation ───────────────────────────────────────────────────────────────
  const managerUrl = (accId: string) =>
    `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accId.replace('act_', '')}`;

  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    ads.forEach(a => { m[a.account_id] = a.partner; });
    return m;
  }, [ads]);

  const visible = useMemo(
    () => ads.filter(a => !excluded.includes(a.account_id) && (!onlyActive || a.status === 'ACTIVE')),
    [ads, onlyActive, excluded]
  );

  const toggleExclude = (accId: string) => {
    setExcluded(ex => ex.includes(accId) ? ex.filter(x => x !== accId) : [...ex, accId]);
    setSelectedPartner(sp => (sp === accId ? null : sp));
  };

  const totals = useMemo(() => {
    const t = visible.reduce(
      (acc, a) => {
        acc.leads += a.leads; acc.spend += a.spend;
        acc.impressions += a.impressions; acc.clicks += a.clicks;
        if (a.status === 'ACTIVE') acc.active += 1;
        return acc;
      },
      { leads: 0, spend: 0, impressions: 0, clicks: 0, active: 0 }
    );
    return {
      ...t,
      ctr: t.impressions ? (t.clicks / t.impressions) * 100 : 0,
      cpl: t.leads ? t.spend / t.leads : null,
      partners: new Set(visible.map(a => a.account_id)).size,
      adCount: visible.length,
    };
  }, [visible]);

  const partners = useMemo<PartnerAgg[]>(() => {
    const map = new Map<string, PartnerAgg>();
    for (const a of visible) {
      let p = map.get(a.account_id);
      if (!p) {
        p = { account_id: a.account_id, partner: a.partner, leads: 0, spend: 0, impressions: 0,
          clicks: 0, adCount: 0, activeCount: 0, ctr: 0, cpl: null, bestAd: null };
        map.set(a.account_id, p);
      }
      p.leads += a.leads; p.spend += a.spend; p.impressions += a.impressions; p.clicks += a.clicks;
      p.adCount += 1; if (a.status === 'ACTIVE') p.activeCount += 1;
      if (!p.bestAd || a.leads > p.bestAd.leads) p.bestAd = a;
    }
    const arr = Array.from(map.values()).map(p => ({
      ...p,
      ctr: p.impressions ? (p.clicks / p.impressions) * 100 : 0,
      cpl: p.leads ? p.spend / p.leads : null,
    }));
    const lOf = (p: PartnerAgg) => realAgg?.partners?.[p.account_id]?.total_leads ?? p.leads;
    const cOf = (p: PartnerAgg) => {
      const rf = realAgg?.partners?.[p.account_id];
      return (rf ? rf.real_cpl : p.cpl) ?? Infinity;
    };
    arr.sort((a, b) => partnerSort === 'cpl' ? cOf(a) - cOf(b) : lOf(b) - lOf(a));
    return arr;
  }, [visible, partnerSort, realAgg]);

  // Echte Lead-Werte (mit Fallback auf die bisherigen Meta-Zahlen, bis geladen)
  const realFor = (accId: string) => realAgg?.partners?.[accId];
  const pLeads = (p: PartnerAgg) => realFor(p.account_id)?.total_leads ?? p.leads;
  const pCpl = (p: PartnerAgg): number | null => {
    const rf = realFor(p.account_id);
    return rf ? rf.real_cpl : p.cpl;
  };
  const maxPartnerLeads = partners.reduce((m, p) => Math.max(m, pLeads(p)), 0) || 1;

  // Echte Gesamtwerte aus den Partner-Werten – berücksichtigt Ausblenden (excluded)
  const realTotals = useMemo(() => {
    if (!realAgg) return null;
    let leads = 0, meta = 0, ff = 0, spend = 0;
    for (const [aid, v] of Object.entries(realAgg.partners)) {
      if (excluded.includes(aid)) continue;
      leads += v.total_leads; meta += v.meta_form_leads; ff += v.fluentform_leads; spend += v.spend;
    }
    return { leads, meta, ff, spend, cpl: leads ? spend / leads : null };
  }, [realAgg, excluded]);

  const winnerAds = useMemo(() => {
    const pool = selectedPartner ? visible.filter(a => a.account_id === selectedPartner) : visible;
    return [...pool].sort((a, b) => b.leads - a.leads);
  }, [visible, selectedPartner]);

  const topPartner = partners[0] || null;
  const topAd = useMemo(() => [...visible].sort((a, b) => b.leads - a.leads)[0] || null, [visible]);
  const winnersToShow = showAllWinners ? winnerAds : winnerAds.slice(0, 9);

  return (
    <Layout user={user} onLogout={onLogout} activePage="analyse" onNavigate={onNavigate}>
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>Analyse</h1>
          <p style={s.subtitle}>Kampagnen-KPIs, Partner-Ranking & Winner-Ads auf einen Blick</p>
        </div>

        <div style={s.controls}>
          <div style={s.searchWrap}>
            <SearchIcon size={16} color="#aeaeb2" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
              placeholder="Kampagne suchen – z.B. Soka, Fitnessselbsttest, Entzündung…"
              style={s.searchInput}
            />
            <button onClick={onSubmit} disabled={loading || !query.trim()} style={s.searchBtn}>
              {loading ? 'Lädt…' : 'Analysieren'}
            </button>
          </div>
          <div style={s.filterBar}>
            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Zeitraum</span>
              <select value={range} onChange={e => changeRange(e.target.value as RangeKey)} style={s.select}>
                {RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <label style={s.activeToggle}>
              <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} />
              Nur aktive Ads
            </label>
          </div>
        </div>

        {error && <div style={s.warningBox}><InfoIcon size={14} color="#92400e" /><span>{error}</span></div>}
        {loading && <div style={s.loadingBox}>Lade Performance-Daten von allen Partnern… das kann einen Moment dauern.</div>}

        {!loading && !submitted && (
          <div style={s.emptyState}>
            <ChartIcon size={32} color="#c8c8d0" />
            <div style={{ color: '#6e6e73', marginTop: 12, maxWidth: 420, textAlign: 'center' as const }}>
              Gib oben eine Kampagne ein (z.B. „Soka") und sieh sofort: Gesamt-Leads, CTR, CPL,
              welche Partner am besten laufen und welche Bild-Ads die Winner sind.
            </div>
          </div>
        )}

        {!loading && submitted && visible.length === 0 && !error && (
          <div style={s.emptyState}>
            <ChartIcon size={32} color="#c8c8d0" />
            <div style={{ color: '#6e6e73', marginTop: 12 }}>
              {ads.length > 0 && onlyActive ? 'Keine aktiven Ads in dieser Auswahl.' : `Keine Ads für „${submitted}" gefunden.`}
            </div>
          </div>
        )}

        {!loading && visible.length > 0 && (
          <>
            {/* ── 1) Kampagnen-KPIs ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
              <SectionTitle>Übersicht „{submitted}"</SectionTitle>
              {realLoading && (
                <span style={s.realLoadingTag}>
                  <span style={s.miniSpinner} /> rufe neue Lead-Daten ab…
                </span>
              )}
            </div>
            <div style={s.kpiGrid}>
              <KpiTile label={realTotals ? 'Echte Leads gesamt' : 'Leads gesamt'} value={nf(realTotals ? realTotals.leads : totals.leads)} accent />
              <KpiTile label="Ø Cost-per-Lead" value={eur(realTotals ? realTotals.cpl : totals.cpl)} />
              <KpiTile label="CTR gesamt" value={pct(totals.ctr)} />
              <KpiTile label="Ausgaben gesamt" value={eur(totals.spend)} />
              <KpiTile label="Impressionen" value={nf(totals.impressions)} />
              <KpiTile label="Klicks" value={nf(totals.clicks)} />
            </div>
            <div style={s.metaLine}>
              {totals.partners} Partner · {totals.adCount} Ads · {totals.active} aktiv
              {realAgg && <span style={{ color: '#1a3a5c', fontWeight: 600 }}> · echte Leads (Meta-Formular + FluentForms, ohne Pixel)</span>}
            </div>

            {/* ── Highlights ── */}
            <div style={s.highlightRow}>
              {topPartner && (
                <div style={s.highlightCard}>
                  <div style={s.highlightLabel}>🏆 Bester Partner</div>
                  <div style={s.highlightMain}>{topPartner.partner}</div>
                  <div style={s.highlightSub}>
                    <strong style={{ color: '#1a3a5c' }}>{nf(pLeads(topPartner))} {realFor(topPartner.account_id) ? 'echte Leads' : 'Leads'}</strong>
                    {pCpl(topPartner) != null && <> · CPL {eur(pCpl(topPartner))}</>} · CTR {pct(topPartner.ctr)}
                  </div>
                </div>
              )}
              {topAd && (
                <div style={s.highlightCard}>
                  <div style={s.highlightLabel}>🥇 Winner-Ad</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {topAd.thumbnail_url
                      ? <img src={topAd.thumbnail_url} alt="" style={s.highlightThumb} onClick={() => setLightboxUrl(topAd.thumbnail_url)} />
                      : <div style={{ ...s.highlightThumb, ...s.thumbPlaceholder }}><ImageIcon size={18} color="#c8c8d0" /></div>}
                    <div style={{ minWidth: 0 }}>
                      <div style={s.highlightMain}>{topAd.ad_name}</div>
                      <div style={s.highlightSub}>
                        {topAd.partner} · <strong style={{ color: '#1a3a5c' }}>{nf(topAd.leads)} Leads</strong>
                        {topAd.cost_per_lead != null && <> · CPL {eur(topAd.cost_per_lead)}</>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── 2) Partner-Ranking ── */}
            <div style={s.sectionHead}>
              <SectionTitle noMargin>Partner-Ranking</SectionTitle>
              <div style={s.sortRow}>
                <span style={s.sortLabel}>Sortieren:</span>
                <button onClick={() => setPartnerSort('leads')} style={partnerSort === 'leads' ? s.segActive : s.seg}>Meiste Leads</button>
                <button onClick={() => setPartnerSort('cpl')} style={partnerSort === 'cpl' ? s.segActive : s.seg}>Bester CPL</button>
              </div>
            </div>
            {excluded.length > 0 && (
              <div style={s.excludedBar}>
                <span style={s.excludedLabel}>Ausgeblendet:</span>
                {excluded.map(id => (
                  <button key={id} onClick={() => toggleExclude(id)} style={s.excludedChip} title="Wieder einblenden">
                    {nameById[id] || id} <span style={{ fontWeight: 700 }}>✕</span>
                  </button>
                ))}
                <button onClick={() => setExcluded([])} style={s.restoreAllBtn}>Alle wieder einblenden</button>
              </div>
            )}
            <div style={s.partnerList}>
              {partners.map((p, i) => {
                const sel = selectedPartner === p.account_id;
                return (
                  <div key={p.account_id}
                    onClick={() => setSelectedPartner(sel ? null : p.account_id)}
                    style={{ ...s.partnerRow, ...(sel ? s.partnerRowSel : {}) }}
                    title="Klicken, um Winner-Ads dieses Partners zu zeigen">
                    <div style={s.rankCell}>
                      {i < 3 ? <span style={{ fontSize: 18 }}>{['🥇', '🥈', '🥉'][i]}</span> : <span style={s.rankNum}>{i + 1}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.partnerName}>
                        <span
                          onClick={e => { e.stopPropagation(); onOpenAccount(p.account_id, p.partner); }}
                          style={s.partnerLink}
                          title="Partner-Profil öffnen"
                        >{p.partner}</span>
                        {p.activeCount > 0 && <span style={s.activePill}>{p.activeCount} aktiv</span>}
                        {(() => {
                          const ws = wpState(p.account_id);
                          if (ws === 'connected') return null;
                          const isErr = ws === 'error';
                          return (
                            <a
                              href={`?account=${encodeURIComponent(p.account_id)}&name=${encodeURIComponent(p.partner)}`}
                              target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ ...s.wpWarn, cursor: 'pointer', textDecoration: 'none', ...(isErr ? { color: '#c05000', background: 'rgba(255,149,0,0.12)' } : {}) }}
                              title={isErr
                                ? 'WordPress-Verbindung fehlerhaft – im neuen Tab im Profil korrigieren'
                                : 'Keine WordPress Verbindung – im neuen Tab im Profil eintragen'}
                            >! {isErr ? 'WP fehlerhaft' : 'keine WP-Verbindung'}</a>
                          );
                        })()}
                      </div>
                      {(() => {
                        const h = wpStatus[p.account_id]?.wp_url;
                        return h ? <div style={s.partnerHost}>{h.replace(/^https?:\/\//, '')}</div> : null;
                      })()}
                      <div style={s.barTrack}>
                        <div style={{ ...s.barFill, width: `${Math.max(3, (pLeads(p) / maxPartnerLeads) * 100)}%` }} />
                      </div>
                    </div>
                    <div style={s.pKpis}>
                      <div style={s.pKpi}><span style={s.pKpiVal}>{nf(pLeads(p))}</span><span style={s.pKpiLbl}>{realFor(p.account_id) ? 'echte Leads' : 'Leads'}</span></div>
                      <div style={s.pKpi}><span style={s.pKpiVal}>{eur(pCpl(p))}</span><span style={s.pKpiLbl}>CPL</span></div>
                      <div style={s.pKpi}><span style={s.pKpiVal}>{pct(p.ctr)}</span><span style={s.pKpiLbl}>CTR</span></div>
                      <div style={s.pKpiWide}><span style={s.pKpiVal}>{eur(p.spend)}</span><span style={s.pKpiLbl}>Ausgaben</span></div>
                    </div>
                    <div style={s.pActions} onClick={e => e.stopPropagation()}>
                      <a href={managerUrl(p.account_id)} target="_blank" rel="noreferrer"
                        style={s.iconBtn} title="Im Werbeanzeigenmanager öffnen">
                        <ExternalLinkIcon size={15} color="#1a3a5c" />
                      </a>
                      <button onClick={() => toggleExclude(p.account_id)} style={s.iconBtn} title="Partner aus dieser Suche ausblenden">
                        <EyeOffIcon size={15} color="#ff3b30" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── 3) Winner-Ads ── */}
            <div style={s.sectionHead}>
              <SectionTitle noMargin>
                Winner-Ads {selectedPartner && <span style={{ fontWeight: 400, color: '#6e6e73', fontSize: 14 }}>· {partners.find(p => p.account_id === selectedPartner)?.partner}</span>}
              </SectionTitle>
              {selectedPartner && (
                <button onClick={() => setSelectedPartner(null)} style={s.clearFilter}>✕ Filter zurücksetzen</button>
              )}
            </div>
            <div style={s.winnerGrid}>
              {winnersToShow.map((ad, i) => (
                <div key={ad.ad_id} style={s.winnerCard}>
                  <div style={s.winnerThumbWrap}>
                    {ad.thumbnail_url
                      ? <img src={ad.thumbnail_url} alt={ad.ad_name} style={s.winnerThumb} onClick={() => setLightboxUrl(ad.thumbnail_url)} />
                      : <div style={{ ...s.winnerThumb, ...s.thumbPlaceholder }}><ImageIcon size={22} color="#c8c8d0" /></div>}
                    {i < 3 && !selectedPartner && <span style={s.winnerMedal}>{['🥇', '🥈', '🥉'][i]}</span>}
                    <span style={ad.status === 'ACTIVE' ? s.badgeActive : s.badgePaused}>
                      {ad.status === 'ACTIVE' ? 'Aktiv' : 'Pausiert'}
                    </span>
                  </div>
                  <div style={s.winnerBody}>
                    <div style={s.winnerName} title={ad.ad_name}>{ad.ad_name}</div>
                    <div style={s.winnerPartner}>{ad.partner}</div>
                    <div style={s.winnerStats}>
                      <span style={s.winnerLeads}>{nf(ad.leads)} Leads</span>
                      <span style={s.winnerSub}>CPL {eur(ad.cost_per_lead)}</span>
                      <span style={s.winnerSub}>CTR {pct(ad.ctr)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {winnerAds.length > 9 && (
              <button onClick={() => setShowAllWinners(v => !v)} style={s.moreBtn}>
                {showAllWinners ? 'Weniger anzeigen' : `Alle ${winnerAds.length} Ads anzeigen`}
              </button>
            )}

            {/* ── Detail-Tabelle (einklappbar) ── */}
            <button onClick={() => setShowTable(v => !v)} style={s.tableToggle}>
              {showTable ? '▲ Detail-Tabelle ausblenden' : '▼ Alle Ads als Detail-Tabelle'}
            </button>
            {showTable && (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr>{['#', 'Ad', 'Partner', 'Status', 'Leads', 'CPL', 'Ausgaben', 'CTR', 'Laufzeit'].map(h =>
                    <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {winnerAds.map((ad, i) => (
                      <tr key={ad.ad_id}>
                        <td style={s.td}>{i + 1}</td>
                        <td style={s.td}>{ad.ad_name}</td>
                        <td style={s.td}>{ad.partner}</td>
                        <td style={s.td}>{ad.status === 'ACTIVE' ? 'Aktiv' : 'Pausiert'}</td>
                        <td style={{ ...s.td, fontWeight: 700, color: '#1a3a5c' }}>{nf(ad.leads)}</td>
                        <td style={s.td}>{eur(ad.cost_per_lead)}</td>
                        <td style={s.td}>{eur(ad.spend)}</td>
                        <td style={s.td}>{pct(ad.ctr)}</td>
                        <td style={s.td}>{ad.runtime_days} T</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {lightboxUrl && (
          <div style={s.lightboxOverlay} onClick={() => setLightboxUrl(null)}>
            <div style={s.lightboxContent} onClick={e => e.stopPropagation()}>
              <button style={s.lightboxClose} onClick={() => setLightboxUrl(null)}>×</button>
              <img src={lightboxUrl} alt="" style={s.lightboxImg} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ── Kleine Komponenten ───────────────────────────────────────────────────────────
function SectionTitle({ children, noMargin }: { children: React.ReactNode; noMargin?: boolean }) {
  return <h2 style={{ ...s.sectionTitle, ...(noMargin ? { margin: 0 } : {}) }}>{children}</h2>;
}
function KpiTile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ ...s.kpiTile, ...(accent ? s.kpiTileAccent : {}) }}>
      <div style={{ ...s.kpiValue, ...(accent ? { color: '#fff' } : {}) }}>{value}</div>
      <div style={{ ...s.kpiLabel, ...(accent ? { color: 'rgba(255,255,255,.8)' } : {}) }}>{label}</div>
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────────
interface IconProps { size?: number; color?: string; }
function SearchIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
}
function ImageIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
}
function InfoIcon({ size = 16, color = '#92400e' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>;
}
function ChartIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
}
function ExternalLinkIcon({ size = 16, color = '#1a3a5c' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>;
}
function EyeOffIcon({ size = 16, color = '#ff3b30' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>;
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: { padding: '40px 48px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 800, color: '#1d1d1f', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { margin: '6px 0 0', color: '#6e6e73', fontSize: 14 },

  controls: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 },
  searchWrap: { display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid #e8e8ed', borderRadius: 10, padding: '6px 8px 6px 14px', background: '#fff', maxWidth: 600 },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1d1d1f', background: 'transparent' },
  searchBtn: { background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  filterBar: { display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' as const },
  filterGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  filterLabel: { fontSize: 12.5, fontWeight: 600, color: '#6e6e73' },
  select: { border: '1.5px solid #e8e8ed', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#1d1d1f', background: '#fff', cursor: 'pointer', outline: 'none' },
  activeToggle: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#3d3d41', cursor: 'pointer' },

  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#1d1d1f', margin: '28px 0 14px' },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '30px 0 14px', flexWrap: 'wrap' as const },

  // KPI tiles
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 },
  kpiTile: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14, padding: '16px 18px' },
  kpiTileAccent: { background: 'linear-gradient(135deg, #1a3a5c, #2d6a9f)', border: 'none' },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1d1d1f', letterSpacing: '-0.5px' },
  kpiLabel: { fontSize: 12, color: '#6e6e73', marginTop: 4, fontWeight: 500 },
  metaLine: { fontSize: 12.5, color: '#aeaeb2', marginTop: 10 },

  // Highlights
  highlightRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginTop: 16 },
  highlightCard: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14, padding: '16px 18px' },
  highlightLabel: { fontSize: 12, fontWeight: 700, color: '#6e6e73', marginBottom: 8 },
  highlightMain: { fontSize: 16, fontWeight: 700, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  highlightSub: { fontSize: 12.5, color: '#6e6e73', marginTop: 3 },
  highlightThumb: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover' as const, border: '1px solid #e8e8ed', cursor: 'pointer', flexShrink: 0 },

  // Sort segmented
  sortRow: { display: 'flex', alignItems: 'center', gap: 6 },
  sortLabel: { fontSize: 12, color: '#aeaeb2', marginRight: 2 },
  seg: { background: '#f5f5f7', color: '#6e6e73', border: 'none', borderRadius: 8, padding: '7px 13px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' },
  segActive: { background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },

  // Partner ranking
  partnerList: { display: 'flex', flexDirection: 'column', gap: 8 },
  partnerRow: { display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #e8e8ed', borderRadius: 12, padding: '12px 16px', cursor: 'pointer', transition: 'border-color .12s, box-shadow .12s' },
  partnerRowSel: { borderColor: '#1a3a5c', boxShadow: '0 0 0 2px rgba(26,58,92,.12)' },
  rankCell: { width: 28, textAlign: 'center' as const, flexShrink: 0 },
  rankNum: { fontSize: 13, fontWeight: 700, color: '#aeaeb2' },
  partnerName: { fontSize: 14, fontWeight: 600, color: '#1d1d1f', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  activePill: { fontSize: 10, fontWeight: 700, color: '#065f46', background: '#d1fae5', borderRadius: 5, padding: '1px 6px' },
  wpWarn: { fontSize: 10, fontWeight: 700, color: '#ff3b30', background: 'rgba(255,59,48,0.10)', borderRadius: 5, padding: '1px 6px' },
  partnerLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#d2d2d7', textUnderlineOffset: 3 },
  partnerHost: { fontSize: 11, color: '#aeaeb2', marginTop: 2, marginBottom: 4 },

  // Lade-Hinweis echte Leads
  realLoadingTag: { display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#1a3a5c', fontWeight: 600 },
  miniSpinner: {
    width: 13, height: 13, border: '2px solid #c7d2dd', borderTop: '2px solid #1a3a5c',
    borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite',
  },
  barTrack: { height: 6, background: '#f0f0f5', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: 6, background: 'linear-gradient(90deg, #1a3a5c, #2d6a9f)', borderRadius: 4 },
  pKpis: { display: 'flex', gap: 18, flexShrink: 0 },
  pKpi: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 52 },
  pKpiWide: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 78 },
  pKpiVal: { fontSize: 14, fontWeight: 700, color: '#1d1d1f' },
  pKpiLbl: { fontSize: 10.5, color: '#aeaeb2', marginTop: 1 },
  pActions: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 4 },
  iconBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid #e8e8ed', background: '#fff', cursor: 'pointer', textDecoration: 'none' },

  excludedBar: { display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: 8, marginBottom: 12, padding: '10px 12px', background: '#fafafa', border: '1px dashed #d2d2d7', borderRadius: 10 },
  excludedLabel: { fontSize: 12, fontWeight: 600, color: '#6e6e73' },
  excludedChip: { fontSize: 12, color: '#3d3d41', background: '#fff', border: '1px solid #e8e8ed', borderRadius: 7, padding: '4px 9px', cursor: 'pointer' },
  restoreAllBtn: { fontSize: 12, color: '#1a3a5c', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600, marginLeft: 'auto' as const },

  // Winner grid
  clearFilter: { background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#6e6e73', cursor: 'pointer' },
  winnerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  winnerCard: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14, overflow: 'hidden' },
  winnerThumbWrap: { position: 'relative' as const, width: '100%', aspectRatio: '1 / 1', background: '#f5f5f7' },
  winnerThumb: { width: '100%', height: '100%', objectFit: 'cover' as const, cursor: 'pointer', display: 'block' },
  thumbPlaceholder: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  winnerMedal: { position: 'absolute' as const, top: 8, left: 8, fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.3))' },
  badgeActive: { position: 'absolute' as const, top: 8, right: 8, fontSize: 10, fontWeight: 700, color: '#065f46', background: 'rgba(209,250,229,.95)', borderRadius: 5, padding: '2px 7px' },
  badgePaused: { position: 'absolute' as const, top: 8, right: 8, fontSize: 10, fontWeight: 700, color: '#92400e', background: 'rgba(254,243,199,.95)', borderRadius: 5, padding: '2px 7px' },
  winnerBody: { padding: '11px 13px' },
  winnerName: { fontSize: 13, fontWeight: 600, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  winnerPartner: { fontSize: 11.5, color: '#aeaeb2', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  winnerStats: { display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8, flexWrap: 'wrap' as const },
  winnerLeads: { fontSize: 15, fontWeight: 800, color: '#1a3a5c' },
  winnerSub: { fontSize: 11.5, color: '#6e6e73' },
  moreBtn: { marginTop: 16, background: '#fff', border: '1.5px solid #1a3a5c', borderRadius: 10, padding: '9px 18px', color: '#1a3a5c', fontSize: 13, fontWeight: 600, cursor: 'pointer' },

  // Detail table
  tableToggle: { display: 'block', marginTop: 22, background: 'transparent', border: 'none', color: '#6e6e73', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 },
  tableWrap: { marginTop: 12, overflowX: 'auto' as const, background: '#fff', border: '1px solid #e8e8ed', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12.5 },
  th: { textAlign: 'left' as const, padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#aeaeb2', textTransform: 'uppercase' as const, borderBottom: '1px solid #f0f0f5', whiteSpace: 'nowrap' as const },
  td: { padding: '9px 12px', borderBottom: '1px solid #f5f5f7', color: '#3d3d41', whiteSpace: 'nowrap' as const },

  // States
  loadingBox: { color: '#6e6e73', fontSize: 13, padding: '40px 0', textAlign: 'center' as const },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 0' },
  warningBox: { background: '#fffbeb', border: '1px solid rgba(245,158,11,.35)', borderRadius: 10, padding: '11px 16px', color: '#92400e', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 },

  // Lightbox
  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  lightboxContent: { position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', alignItems: 'center' },
  lightboxImg: { maxWidth: '80vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain' as const },
  lightboxClose: { position: 'absolute', top: -44, right: 0, background: 'transparent', border: 'none', color: '#fff', fontSize: 34, cursor: 'pointer', lineHeight: 1, padding: 0 },
};
